import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { CalendarDays, LogOut, Moon, Plus, Settings2, ShieldCheck, Sun, Ticket, Users } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/", label: "Tichete", icon: Ticket, end: true, id: "tickets" },
  { to: "/programari", label: "Programări", icon: CalendarDays, end: true, id: "appointments" },
  { to: "/nou", label: "Tichet nou", icon: Plus, id: "new" },
  { to: "/clienti", label: "Clienți", icon: Users, id: "clients" },
  { to: "/echipa", label: "Echipă", icon: ShieldCheck, admin: true, id: "team" },
  { to: "/programari/setari", label: "Personal & servicii", icon: Settings2, admin: true, desktopOnly: true, id: "schedule-settings" },
];

export const applyTheme = (t) => {
  document.documentElement.classList.toggle("dark", t === "dark");
  localStorage.setItem("sp_theme", t);
};

const ThemeToggle = () => {
  const [theme, setTheme] = useState(localStorage.getItem("sp_theme") || "dark");
  const toggle = () => {
    const next = theme === "dark" ? "light" : "dark";
    applyTheme(next);
    setTheme(next);
  };
  return (
    <button data-testid="theme-toggle-button" onClick={toggle} className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground" aria-label="Schimbă tema">
      {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
    </button>
  );
};

const Logo = () => (
  <div className="flex items-center gap-2.5">
    <img src="/logo.png" alt="Drag de Lemn" className="h-10 w-10 object-contain" data-testid="app-logo" />
    <div className="leading-tight">
      <div className="font-display text-base font-bold tracking-tight">DDL Hub</div>
      <div className="text-[10px] font-medium uppercase tracking-[0.2em] text-muted-foreground">Drag de Lemn</div>
    </div>
  </div>
);

export const PageHeader = ({ title, subtitle, children }) => (
  <div className="mb-6 flex flex-col gap-4 sm:mb-8 sm:flex-row sm:items-end sm:justify-between">
    <div>
      <h1 className="text-3xl font-extrabold tracking-tight sm:text-4xl">{title}</h1>
      {subtitle && <p className="mt-1 text-sm text-muted-foreground sm:text-base">{subtitle}</p>}
    </div>
    {children && <div className="flex flex-wrap gap-2">{children}</div>}
  </div>
);

const Sidebar = ({ items, user, onLogout }) => (
  <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 flex-col border-r bg-card/60 backdrop-blur-xl lg:flex">
    <div className="p-6"><Logo /></div>
    <nav className="flex-1 space-y-1 px-3">
      {items.map((n) => (
        <NavLink key={n.to} to={n.to} end={n.end} data-testid={`nav-${n.id}`}
          className={({ isActive }) => cn("flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
            isActive ? "bg-primary text-primary-foreground shadow-md shadow-primary/20" : "text-muted-foreground hover:bg-accent hover:text-foreground")}>
          <n.icon className="h-4 w-4" />{n.label}
        </NavLink>
      ))}
    </nav>
    <div className="m-3 flex items-center gap-3 rounded-xl border bg-background/50 p-3">
      <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-primary/15 font-display text-sm font-bold text-primary">
        {user.name?.[0]?.toUpperCase()}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold" data-testid="current-user-name">{user.name}</div>
        <div className="text-xs capitalize text-muted-foreground">{user.role}</div>
      </div>
      <ThemeToggle />
      <button data-testid="logout-button" onClick={onLogout} className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive" aria-label="Deconectare">
        <LogOut className="h-4 w-4" />
      </button>
    </div>
  </aside>
);

const MobileBar = ({ items }) => (
  <nav className="fixed bottom-0 left-0 right-0 z-40 border-t bg-card/95 px-2 pb-[max(env(safe-area-inset-bottom),0.5rem)] pt-2 backdrop-blur-md lg:hidden no-print">
    <div className="flex items-end justify-around">
      {items.filter((n) => !n.desktopOnly).map((n) => (
        <NavLink key={n.to} to={n.to} end={n.end} data-testid={`mobile-nav-${n.id}`}
          className={({ isActive }) => cn("flex flex-col items-center gap-1 px-1.5 text-[11px] font-medium transition-colors",
            n.id === "new" ? "-mt-6" : isActive ? "text-primary" : "text-muted-foreground")}>
          {n.id === "new" ? (
            <span className="grid h-14 w-14 place-items-center rounded-full bg-primary text-primary-foreground shadow-xl shadow-primary/40 transition-transform active:scale-95">
              <Plus className="h-6 w-6" />
            </span>
          ) : <n.icon className="h-5 w-5" />}
          <span>{n.label}</span>
        </NavLink>
      ))}
    </div>
  </nav>
);

export const Layout = () => {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const items = NAV.filter((n) => !n.admin || user.role === "admin");
  const onLogout = async () => {
    await logout();
    nav("/login");
  };

  return (
    <div className="min-h-screen bg-background bg-grid">
      <Sidebar items={items} user={user} onLogout={onLogout} />
      <header className="sticky top-0 z-30 flex items-center justify-between border-b bg-background/80 px-4 py-3 backdrop-blur-md lg:hidden no-print">
        <Logo />
        <div className="flex items-center gap-1">
          <ThemeToggle />
          <button data-testid="mobile-logout-button" onClick={onLogout} className="rounded-lg p-2 text-muted-foreground hover:text-destructive" aria-label="Deconectare">
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </header>
      <main className="lg:pl-64">
        <div className="mx-auto max-w-7xl px-4 py-6 pb-32 sm:px-6 lg:px-10 lg:py-10">
          <Outlet />
        </div>
      </main>
      <MobileBar items={items} />
    </div>
  );
};
